/* eslint-disable no-param-reassign */
import { SponsoredProvider } from '@digital-retail/sponsored-provider-library';
import { isStandalone, isTottusStandalone } from '@digital-retail/store-manager';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { AutoCorrect } from '../../components/AutoCorrect/AutoCorrect';
import { BrandTitle } from '../../components/BrandTitle/BrandTitle';
import { CategoryBanner } from '../../components/CategoryBanner/CategoryBanner';
import { CategoryTitle } from '../../components/CategoryTitle/CategoryTitle';
import Facets from '../../components/Facets/Facets';
import GoogleAdComponent from '../../components/GoogleAds';
import { InterlinkingFooter } from '../../components/InterlinkingFooter/InterlinkingFooter';
import PromotedFilters from '../../components/PromotedFilters/PromotedFilters';
import SelectedFilters from '../../components/SelectedFilters/SelectedFilters';
import SponsorDisplayLeftWrapper from '../../components/SponsorDisplayLeftWrapper';
import SurveyBar from '../../components/SurveyBar/SurveyBar';
import { BodyCopy } from '../../components/ui/BodyCopy/BodyCopy';
import MatchMedia from '../../components/ui/MatchMedia/MatchMedia';
import { constants } from '../../config/constants';
import { withApplicationContext } from '../../utils/ApplicationContext';
import { withCart } from '../../utils/CartContext';
import { useShouldHideSponsoredDisplay } from '../../utils/hooks/UseShouldHideSponsoredPod';
import { isInterlinkingEnabled } from '../../utils/interlinking/helpers/toggles';
import { scrollToTopProductList } from '../../utils/scrollToProductList';
import SearchResults from '../SearchResults/SearchResults';
import {
  getAdInjectorEnabled,
  getFacetParams,
  getHandleBannerActionForSponsorDisplay,
  getIsSponsoredDisplayEnabled,
  getUrlBase,
} from '../SearchResults/utils';
import { ProductLandingContainerStyles } from './ProductLandingContainer.style';

const { publicRuntimeConfig } = getConfig();

const ProductLandingContainer = ({
  layout,
  products,
  facets,
  promotedFilter,
  appCtx,
  appliedFacets,
  availabilityFacets,
  baseFacetsURL,
  autoCorrections,
  pagination,
  sortOptions,
  searchTerm,
  categoryParentDetails,
  brandDetails,
  categoryBannerHtml,
  leftPromotionContent,
  pageName,
  cart,
  collectionId,
  canonicalUrl,
  breadCrumbData,
}) => {
  const { query = {} } = useRouter();
  const { id = '', name = '', Ntt = '' } = query;
  const isDesktop = appCtx.deviceType === 'desktop';
  const labels = appCtx?.siteConfig?.textDictionary ?? {};

  const productListRef = useRef();

  const [stopScroll, setStopScroll] = useState(true);

  const preSelectedStore =
    availabilityFacets
      ?.find((facet) => constants.SHIPPING_GROUP_REGEX.test(facet.group))
      ?.values?.find((f) => /(.bas_)/.test(f.facetType) && f.selected)
      ?.title?.replace(constants.REGEX_1P, '') ?? null;

  const [selectedStore, setSelectedStore] = useState(preSelectedStore);

  const onFacetChange = (obj) => {
    const displayTitle = obj.title.replace(constants.REGEX_1P, '');
    setSelectedStore(displayTitle);
    setStopScroll(false);
  };

  const handleScrollToTopProductList = (selectedURLs) => {
    if (!stopScroll) {
      scrollToTopProductList(selectedURLs, productListRef, appCtx.deviceType);
    }
  };

  const visibleFilters = appliedFacets.filter((f) => f.visible);

  const selectedFiltersLabel = labels.SELECTED_FILTERS;
  const resultsLabel = labels.RESULTS;

  const [showFacets, setShowFacets] = useState(true);
  const { regionCode, store } = appCtx;
  const isSearchPage = pageName === 'search';
  const isSponsoredDisplayEnabled = getIsSponsoredDisplayEnabled(appCtx, Ntt, id);
  const adInjectorIsEnabled = getAdInjectorEnabled(appCtx);
  const [hasImpressionBeenSent, setHasImpressionBeenSent] = useState(false);
  const facetParams = getFacetParams();
  const configValue = {
    regionCode: appCtx.regionCode,
    deviceType: appCtx.deviceType,
    zones: appCtx.zones,
    site: appCtx.store,
    store: appCtx.store,
    urlBase: getUrlBase(publicRuntimeConfig),
    facetParams,
    ...(isSearchPage ? {} : { categoryId: id, categoryName: name }),
    isOrchestratorEnabled: appCtx.siteConfig?.toggles?.isOrchestratorEnabled ?? false,
    priceGroupId: appCtx.priceGroupId,
    politicalId: appCtx.politicalId,
    toggles: {
      disableSponsoredDisplay: appCtx.siteConfig?.toggles?.disableSponsoredDisplay ?? false,
      disableSponsoredBrands: appCtx.siteConfig?.toggles?.disableSponsoredBrands ?? false,
      disableSponsoredProducts: appCtx.siteConfig?.toggles?.disableSponsoredProducts ?? false,
      disableSponsoredStrip: appCtx.siteConfig?.toggles?.disableSponsoredStrip ?? false,
      disableSponsoredCarousel: appCtx.siteConfig?.toggles?.disableSponsoredCarousel ?? false,
      disableSponsoredProductBanner: appCtx.siteConfig?.toggles?.disableSponsoredProductBanner ?? false,
    },
  };
  const handleBannerAction = getHandleBannerActionForSponsorDisplay({
    appCtx,
    config: publicRuntimeConfig,
    categoryName: name,
    categoryId: id,
    mPos: 10,
    hasImpressionBeenSent,
    setHasImpressionBeenSent,
  });
  const shouldHideSDForKiosk = useShouldHideSponsoredDisplay(appCtx);
  const promoContent =
    leftPromotionContent ||
    (isStandalone({ store }) && pageName === 'category' && ['cl', 'pe'].includes(regionCode)
      ? appCtx.siteConfig?.textDictionary?.CATEGORY_LATERAL_TEXT?.replace(
          /%Category Name%/g,
          categoryParentDetails[0].label
        )
      : '');

  const desktop = () => {
    if (visibleFilters.length > 0) {
      return (
        <SelectedFilters
          filters={visibleFilters}
          appCtx={appCtx}
          baseFacetsURL={baseFacetsURL}
          selectedFiltersLabel={selectedFiltersLabel}
          pageName={pageName}
          facets={facets}
        />
      );
    }
    return null;
  };

  const l1Category =
    isTottusStandalone(store) && breadCrumbData.length === 1 ? categoryParentDetails[0] : categoryParentDetails[1];
  const l2Category = isTottusStandalone(store) && breadCrumbData.length === 1 ? '' : categoryParentDetails[0];

  return (
    <SponsoredProvider config={configValue}>
      <div className="pod-group">
        {adInjectorIsEnabled && !categoryBannerHtml && (
          <div className="google-ad-top">
            <GoogleAdComponent adsPosition={constants.GOOGLE_AD_POSITION.TOP} deviceType={isDesktop} />
          </div>
        )}
        {categoryBannerHtml && (
          <div className="pad-group--category-banner container">
            <CategoryBanner categoryBannerHtml={categoryBannerHtml} store={appCtx.store} />
          </div>
        )}
        {autoCorrections.length !== 0 && (
          <div className="pad-group--auto-correct-mkp container">
            <AutoCorrect searchTerm={searchTerm} items={autoCorrections} appCtx={appCtx} />
          </div>
        )}
        <div className="pod-group--container container">
          <section className="pod-group--filters">
            {pageName === 'category' ? (
              <CategoryTitle
                appCtx={appCtx}
                appliedFacets={appliedFacets}
                level1Category={l1Category}
                level2Category={l2Category}
                resultsLabel={resultsLabel}
                paginationCount={pagination.data.count}
                breadCrumbData={breadCrumbData}
              />
            ) : (
              <BrandTitle
                brandTitle={brandDetails.displayName}
                resultsLabel={resultsLabel}
                paginationCount={pagination.data.count}
                deviceType={appCtx.deviceType}
              />
            )}
            {appCtx.deviceType !== 'mobile' && (
              <div className="total-results">
                <BodyCopy size="copy5" id="testId-ProductLandingContainer-totalResults" type="secondary">
                  {resultsLabel} ({pagination.data.count})
                </BodyCopy>
              </div>
            )}
            <MatchMedia
              deviceType={appCtx.deviceType}
              content={{
                desktop,
              }}
            />
            {showFacets && (
              <Facets
                availabilityFacets={availabilityFacets}
                baseFacetsURL={baseFacetsURL}
                sortOptions={sortOptions}
                data={facets}
                pageName={pageName}
                onFacetChange={onFacetChange}
                handleScrollToTopProductList={handleScrollToTopProductList}
                appliedFacets={appliedFacets}
                pagination={pagination}
                canonicalUrl={canonicalUrl}
              />
            )}

            {!isTottusStandalone(store) && (
              <div className="survey-container">
                <SurveyBar withBorder />
              </div>
            )}

            <div className="vertical-banner">
              {isSponsoredDisplayEnabled && isDesktop && !shouldHideSDForKiosk && (
                <SponsorDisplayLeftWrapper handleBannerAction={handleBannerAction} appCtx={appCtx} />
              )}
            </div>
            {adInjectorIsEnabled && isDesktop && (
              <div className="google-ad-side">
                <GoogleAdComponent adsPosition={constants.GOOGLE_AD_POSITION.SIDE} deviceType={isDesktop} />
              </div>
            )}
          </section>
          <section className="pod-group--products" ref={productListRef}>
            {promotedFilter && (
              <PromotedFilters promotedFilter={promotedFilter} baseFacetsURL={baseFacetsURL} pageName={pageName} />
            )}
            <SearchResults
              layout={layout}
              products={products}
              pagination={pagination}
              sortOptions={sortOptions}
              pageName={pageName}
              tenant={appCtx.tenant}
              cart={cart}
              setShowFacets={setShowFacets}
              setStopScroll={setStopScroll}
              collectionId={collectionId}
              leftPromotionContent={promoContent}
              selectedStore={selectedStore}
            />
          </section>
        </div>
        {!isTottusStandalone(store) && (
          <div className="survey-container-mobile">
            <SurveyBar />
          </div>
        )}
        <style jsx>{ProductLandingContainerStyles}</style>
      </div>
      <InterlinkingFooter
        title={appCtx?.interlinking?.title || ''}
        groups={appCtx?.interlinking?.groups || []}
        isEnabled={isInterlinkingEnabled(appCtx?.siteConfig?.toggles)}
      />
    </SponsoredProvider>
  );
};

ProductLandingContainer.defaultProps = {
  products: [],
  facets: [],
  appliedFacets: [],
  searchTerm: '',
  layout: {
    desktop: {
      view: '4_GRID',
    },
    mobile: {
      view: '2_GRID',
    },
  },
  categoryParentDetails: [{}, {}],
  leftPromotionContent: '',
  categoryBannerHtml: '<div></div>',
  pageName: 'category',
  brandDetails: {},
  collectionId: '',
  canonicalUrl: '',
  breadCrumbData: [],
};
ProductLandingContainer.propTypes = {
  products: PropTypes.arrayOf(PropTypes.object),
  facets: PropTypes.arrayOf(PropTypes.object),
  appliedFacets: PropTypes.arrayOf(PropTypes.object),
  appCtx: PropTypes.object.isRequired,
  pagination: PropTypes.shape({
    data: PropTypes.object,
    onPageSelection: PropTypes.func,
  }).isRequired,
  sortOptions: PropTypes.array.isRequired,
  searchTerm: PropTypes.string,
  baseFacetsURL: PropTypes.string.isRequired,
  autoCorrections: PropTypes.array.isRequired,
  availabilityFacets: PropTypes.array.isRequired,
  layout: PropTypes.string,
  categoryParentDetails: PropTypes.arrayOf(PropTypes.object),
  categoryBannerHtml: PropTypes.string,
  leftPromotionContent: PropTypes.string,
  promotedFilter: PropTypes.object.isRequired,
  pageName: PropTypes.string,
  brandDetails: PropTypes.object,
  cart: PropTypes.object.isRequired,
  collectionId: PropTypes.string,
  canonicalUrl: PropTypes.string,
  breadCrumbData: PropTypes.arrayOf(PropTypes.object),
};

export default withApplicationContext(withCart(ProductLandingContainer));
export { ProductLandingContainer };
